import React, { useState } from 'react';
import type { ProfileAction } from '../../../services/authService';

interface ActionChecklistProps {
    moduleCode: string;
    actions: ProfileAction[];
    onChange: (activeActionCodes: string[]) => void;
}

const ACTION_CODE_REGEX = /^[a-z][a-z0-9_]*$/;
const MAX_ACTION_CODE_LENGTH = 50;

/**
 * Renders a checklist of actions for a given module.
 * Each action is shown as a checkbox (checked if active).
 * Includes an input to add custom action codes.
 */
const ActionChecklist: React.FC<ActionChecklistProps> = ({
    moduleCode: _moduleCode,
    actions,
    onChange,
}) => {
    const [customCode, setCustomCode] = useState('');
    const [customError, setCustomError] = useState<string | null>(null);

    const activeCodes = new Set(
        actions.filter((a) => a.is_active).map((a) => a.action_code),
    );

    const handleToggle = (actionCode: string) => {
        const next = new Set(activeCodes);
        if (next.has(actionCode)) {
            next.delete(actionCode);
        } else {
            next.add(actionCode);
        }
        onChange(Array.from(next));
    };

    const handleAddCustom = () => {
        const code = customCode.trim();
        setCustomError(null);

        if (!code) return;

        if (!ACTION_CODE_REGEX.test(code)) {
            setCustomError('Formato inválido. Use solo letras minúsculas, números y guion bajo.');
            return;
        }

        if (code.length > MAX_ACTION_CODE_LENGTH) {
            setCustomError(`Máximo ${MAX_ACTION_CODE_LENGTH} caracteres.`);
            return;
        }

        if (actions.some((a) => a.action_code === code)) {
            setCustomError('Esta acción ya existe.');
            return;
        }

        const next = new Set(activeCodes);
        next.add(code);
        onChange(Array.from(next));
        setCustomCode('');
    };

    return (
        <div className="action-checklist">
            <div className="d-flex flex-column gap-2 mb-2">
                {actions.map((action) => (
                    <label key={action.id} className="d-flex align-items-center gap-2 mb-0">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={action.is_active}
                            onChange={() => handleToggle(action.action_code)}
                            aria-label={action.action_code}
                        />
                        <span className="small">{action.action_code}</span>
                    </label>
                ))}
            </div>

            <div className="input-group input-group-sm">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Agregar acción personalizada"
                    value={customCode}
                    onChange={(e) => {
                        setCustomCode(e.target.value);
                        setCustomError(null);
                    }}
                    maxLength={MAX_ACTION_CODE_LENGTH}
                />
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleAddCustom}
                >
                    Agregar
                </button>
            </div>
            {customError && (
                <div className="text-danger small mt-1">{customError}</div>
            )}
        </div>
    );
};

export default ActionChecklist;
